import type { RainbowKitWalletConnectParameters, Wallet } from '@rainbow-me/rainbowkit';
import { createConnector } from 'wagmi';
import { metaMask } from 'wagmi/connectors';

const METAMASK_ICON =
  'https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg';

type Options = {
  projectId: string; // required by RainbowKit getDefaultConfig; unused here (no WC fallback)
  walletConnectParameters?: RainbowKitWalletConnectParameters;
};

/**
 * RainbowKit's `metaMaskWallet` only wires the MetaMask SDK connector when
 * `isMetaMask(window.ethereum)` is true. If Phantom (or another wallet) owns
 * `window.ethereum`, it falls back to WalletConnect with your `projectId` — with a
 * placeholder id that never completes, so Connect → MetaMask spins forever.
 *
 * This entry always uses wagmi's `metaMask()` connector (MetaMask SDK discovers the
 * extension via EIP-6963 / SDK init, not via the default injected provider).
 */
export function metaMaskSdkOnlyWallet({ projectId: _projectId, walletConnectParameters }: Options): Wallet {
  const isCoarsePointer =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches;

  return {
    id: 'metaMask',
    name: 'MetaMask',
    rdns: 'io.metamask',
    iconUrl: METAMASK_ICON,
    iconAccent: '#f6851a',
    iconBackground: '#fff',
    downloadUrls: {
      android: 'https://play.google.com/store/apps/details?id=io.metamask',
      ios: 'https://apps.apple.com/us/app/metamask/id1438144202',
      mobile: 'https://metamask.io/download',
      qrCode: 'https://metamask.io/download',
      chrome:
        'https://chrome.google.com/webstore/detail/metamask/nkbihfbeogaeaoehlefnkodbefgpgknn',
      edge: 'https://microsoftedge.microsoft.com/addons/detail/metamask/ejbalbakoplchlghecdalmeeeajnimhm',
      firefox: 'https://addons.mozilla.org/firefox/addon/ether-metamask',
      opera: 'https://addons.opera.com/extensions/details/metamask-10',
      browserExtension: 'https://metamask.io/download',
    },
    extension: {
      instructions: {
        learnMoreUrl: 'https://metamask.io/faqs/',
        steps: [
          {
            description:
              'Install the MetaMask extension for your browser from the official site.',
            step: 'install',
            title: 'Install MetaMask',
          },
          {
            description: 'Create a new wallet or import an existing one.',
            step: 'create',
            title: 'Create a wallet',
          },
          {
            description: 'Refresh the page and connect again.',
            step: 'refresh',
            title: 'Refresh the page',
          },
        ],
      },
    },
    createConnector: (walletDetails) =>
      createConnector((config) => {
        const metamaskConnector = metaMask({
          enableAnalytics: false,
          checkInstallationImmediately: false,
          headless: true,
          useDeeplink: isCoarsePointer,
          dappMetadata: {
            connector: 'rainbowkit',
            name: walletConnectParameters?.metadata?.name,
            iconUrl: walletConnectParameters?.metadata?.icons?.[0],
            url: walletConnectParameters?.metadata?.url,
          },
        })(config);
        return {
          ...metamaskConnector,
          ...walletDetails,
          getChainId: async () => {
            try {
              return await metamaskConnector.getChainId();
            } catch {
              return config.chains[0]?.id ?? 1;
            }
          },
        };
      }),
  };
}
